// ./src/config/userRoles.ts
import { OPERATION_RIGHTS } from './operationRights';

// ============================================
// Roles
// ============================================
// Only OWNER and ADMIN exist today. To add a role later:
//   1. add it here,
//   2. add it to the relevant ACCESS groups below,
//   3. add one entry to USER_ROLE_RIGHTS_ENTITIES (or reuse a preset).
// Nothing else needs to change — the sidebar and pages read from these.

export const USER_ROLES = {
  NO_ROLE: '',
  OWNER: 'f3954ab2-df0b-47bb-8081-24332c2ffdd8',
  ADMIN: '4becf9f6-b8e7-4952-bc5a-73e77caad09b', // everything except the account's settings, closing it, the subscription and payouts (owner's — 2026-10-16)
  // A FE-only synthetic role for a NON-account crew session (docs/CREW-ACCESS.md,
  // scenario 3). NOT a real ms_auth role — it exists so the event-workspace routes
  // render for a password-crew session; the BE authorizes every action by the crew
  // token, and CrewAccessContext filters the nav to the caller's real capabilities.
  CREW: 'crew-session',
  // Future: MANAGER (per-event), STAFF (door/check-in), FINANCE, MARKETING, ANALYST…
};

/**
 * 2026-10-28 — EVERY BRAND HAS ITS OWN ROW OF EACH ROLE (ms_auth keeps roles per space), so PartyLabz's "Account Owner"
 * is not the id `USER_ROLES.OWNER` names — its owners got "Access Denied — Account Owner". Each brand's row of each role
 * this app names. ⚠ The API's `BRAND_ROLES` (boundary/userRoles.ts) is the catalog `provision:roles` writes;
 * `check-role-ids` compares this table with it and reads ms_auth's live roles.
 */
export const BRAND_ROLE_IDS: Record<string, { OWNER: string; ADMIN: string }> = {
  eventsmagic: { OWNER: USER_ROLES.OWNER, ADMIN: USER_ROLES.ADMIN },
  partylabz: { OWNER: 'bbec1003-1647-4b00-9172-f1bf8b3813c4', ADMIN: '202e6b43-fef7-4fba-900b-21b792b5e68e' },
};

/** Each brand's row → the role `USER_ROLES` names (derived; the role's own id is not an alias). */
export const USER_ROLE_ALIASES: Record<string, string> = Object.fromEntries(
  Object.values(BRAND_ROLE_IDS).flatMap((ids) =>
    (['OWNER', 'ADMIN'] as const).filter((role) => ids[role] !== USER_ROLES[role]).map((role) => [ids[role], USER_ROLES[role]]),
  ),
);

/** THIS brand's row of a role `USER_ROLES` names — what to SEND (an invitation's role); an unknown brand or role: itself. */
export const brandRoleId = <T extends string | null | undefined>(role: T, spaceId: string | null | undefined): T | string => {
  const ids = BRAND_ROLE_IDS[String(spaceId ?? '')];
  const key = (['OWNER', 'ADMIN'] as const).find((name) => USER_ROLES[name] === role);

  return ids && key ? ids[key] : role;
};

/** The role `USER_ROLES` names for any brand's row of it (an unknown id is itself). Pure. */
export const canonicalRoleId = (roleId: string | null | undefined): string => {
  const id = String(roleId ?? '');

  return USER_ROLE_ALIASES[id] ?? id;
};

export const USER_ROLE_OPTIONS = [
  {
    value: USER_ROLES.ADMIN,
    label: 'Admin',
    disabled: false,
  },
] as const;

export const MANAGER_ROLES = [USER_ROLES.OWNER, USER_ROLES.ADMIN];
export const ALL_ROLES = [USER_ROLES.OWNER, USER_ROLES.ADMIN];
export const MAIN_ROLES_WHO_CAN_EDIT = [USER_ROLES.OWNER, USER_ROLES.ADMIN];

// ============================================
// CREW roles (docs/CREW-ACCESS.md) — the event guest_role values that count as
// crew. These are event-scoped ROLES (mirrors the API's EVENT_GUEST_ROLE): when a
// viewer is a crew member of the event they're looking at, the workspace nav is
// filtered by their crew role instead of their account role (see the event-page
// access groups below + DashboardLayout). The BE authorizes every action by the
// crew capability gate (these strings are the FE nav/route key only).
// ============================================
export const CREW_ROLES = {
  HOST: 'host',
  CO_HOST: 'co_host',
  ORGANIZER: 'organizer',
  STAFF: 'staff',
  // A venue's person at a voucher stall (2026-10-18) — Redeem Vouchers ONLY; in no other ACCESS list.
  VOUCHER_REDEEMER: 'voucher_redeemer',
  VOLUNTEER: 'volunteer',
  VENDOR: 'vendor',
  SPEAKER: 'speaker',
  PERFORMER: 'performer',
  OTHER: 'other',
} as const;

// Crew role bundles used to build the event-page access groups.
// A near-full delegate (host/co_host) sees every event page except delete; an
// organizer sees the operational pages; staff/volunteer see the guest-facing subset.
const CREW_NEAR_FULL = [CREW_ROLES.HOST, CREW_ROLES.CO_HOST];
const CREW_OPERATIONAL = [...CREW_NEAR_FULL, CREW_ROLES.ORGANIZER];
const CREW_ALL = [
  ...CREW_OPERATIONAL,
  CREW_ROLES.STAFF,
  CREW_ROLES.VOLUNTEER,
  CREW_ROLES.VENDOR,
  CREW_ROLES.SPEAKER,
  CREW_ROLES.PERFORMER,
  CREW_ROLES.OTHER,
];

// ============================================
// ACCESS groups — referenced by the sidebar (siteMap `access` / `roles`).
// Named groups instead of inline role arrays so a new role is added in
// one place, not on every menu item.
// ============================================

export const ACCESS = {
  /** Anyone signed in. */
  ALL: ALL_ROLES,
  /** Account-wide administration (settings, team, legal, branding). */
  ACCOUNT_ADMIN: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  /** Create / manage events and templates + the DEFAULT for event-workspace pages.
   *  Includes the synthetic CREW role so a password-crew session can reach the event
   *  routes (route rendering is permissive; the BE gates each action), PLUS the
   *  OPERATIONAL crew roles (host / co_host / organizer) so those roles SEE every
   *  standard event page in crew mode. staff/volunteer are NOT here — they only get
   *  the guest-facing pages below. Account-scope pages that use this group are never
   *  shown in crew mode (crew mode is an event workspace), so the crew roles here are
   *  inert there. */
  EVENT_MANAGE: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.CREW, ...CREW_OPERATIONAL],
  /** Event pages every crew role can see (event overview, guest list) — adds staff +
   *  volunteer + external roles on top of the operational default. */
  EVENT_VIEW: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.CREW, ...CREW_ALL],
  /** #68 Lost & found — operational crew + desk staff (not volunteers; they hand things to the desk). Mirrors lostfound.manage. */
  EVENT_LOST_FOUND: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.CREW, ...CREW_OPERATIONAL, CREW_ROLES.STAFF],
  /** Check-in console — operational crew + door staff (not volunteers). */
  EVENT_CHECKIN: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.CREW, ...CREW_OPERATIONAL, CREW_ROLES.STAFF],
  /** Redeem Vouchers — the check-in crew, plus a stall's voucher redeemer (2026-10-18), whose ONLY page this is. */
  EVENT_VOUCHER_REDEEM: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.CREW, ...CREW_OPERATIONAL, CREW_ROLES.STAFF, CREW_ROLES.VOUCHER_REDEEMER],
  /** Guest list — operational crew + staff + volunteer (view; edit gated by capability/BE). */
  EVENT_GUESTS: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.CREW, ...CREW_OPERATIONAL, CREW_ROLES.STAFF, CREW_ROLES.VOLUNTEER],
  /** Near-full delegate only (event settings, website, crew management) — host/co_host,
   *  NOT organizer. Owner/admin always. */
  EVENT_ADMIN: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.CREW, ...CREW_NEAR_FULL],
  /** Deleting the event — account owner/admin ONLY, never crew. */
  EVENT_DELETE: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  /** Audience / CRM (contacts, groups, memberships, tags). */
  AUDIENCE: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  /** Shared directories (venues, sponsor packages, catalogs). */
  DIRECTORY: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  /** Design assets (themes). */
  DESIGN: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  /** Money — payments, taxes. */
  FINANCE: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  /** Subscription & billing — owner only. */
  BILLING: [USER_ROLES.OWNER],
  /**
   * The account's own settings and closing it — owner only (owner, 2026-10-16: "only account owner can do update account
   * settings and close the account, and manage subscription. Admin can do everything else"). ms_auth refuses anyone
   * else on its side (`can_manage_account` / `can_close_account`, migration 380); this keeps the page from offering it.
   */
  ACCOUNT_OWNER: [USER_ROLES.OWNER],
};

// ============================================
// Entities — the source of truth for per-entity CRUD rights per role.
// Pages call hasRightTo(ENTITIES.X, OPERATION_RIGHTS.Y).
// ============================================

export const ENTITIES = {
  // Account scope
  EVENTS: 'EVENTS',
  EVENT_TEMPLATES: 'EVENT_TEMPLATES',
  CONTACTS: 'CONTACTS',
  CONTACT_GROUPS: 'CONTACT_GROUPS',
  MEMBERSHIPS: 'MEMBERSHIPS',
  TAGS: 'TAGS',
  VENUES: 'VENUES',
  PARTNER_PACKAGES: 'PARTNER_PACKAGES',
  HIGHLIGHTS: 'HIGHLIGHTS',
  CATEGORIES: 'CATEGORIES',
  VOUCHERS: 'VOUCHERS',
  RENTAL_ASSETS: 'RENTAL_ASSETS',
  INVENTORY_ITEMS: 'INVENTORY_ITEMS',
  ARTISTS: 'ARTISTS',
  PURCHASE_ORDERS: 'PURCHASE_ORDERS',
  VOLUNTEER_ROLES: 'VOLUNTEER_ROLES',
  MARKETING_CHANNELS: 'MARKETING_CHANNELS',
  THEMES: 'THEMES',
  PAYMENTS: 'PAYMENTS',
  TAX_SETTINGS: 'TAX_SETTINGS',
  POLICIES: 'POLICIES',
  USERS: 'USERS',
  USER_INVITATIONS: 'USER_INVITATIONS',
  ORGANIZER_PROFILE: 'ORGANIZER_PROFILE',
  WHITE_LABEL: 'WHITE_LABEL',
  ACCOUNT_SETTINGS: 'ACCOUNT_SETTINGS',

  // Event scope
  EVENT_DETAILS: 'EVENT_DETAILS',
  EVENT_WEBSITE: 'EVENT_WEBSITE',
  EVENT_HIGHLIGHTS: 'EVENT_HIGHLIGHTS',
  POINTS_OF_INTEREST: 'POINTS_OF_INTEREST',
  ACCOMMODATIONS: 'ACCOMMODATIONS',
  EVENT_VOLUNTEERS: 'EVENT_VOLUNTEERS',
  SIGNUP_ITEMS: 'SIGNUP_ITEMS',
  POLL_QUESTIONS: 'POLL_QUESTIONS',
  NOTES: 'NOTES',
  REMINDERS: 'REMINDERS',
  TASKS: 'TASKS',
  BUDGET_ITEMS: 'BUDGET_ITEMS',
  RENTALS: 'RENTALS',
  QR_TAGS: 'QR_TAGS',
  LOST_FOUND: 'LOST_FOUND', // #68 — found items and lost reports of an event
  EVENT_VENDORS: 'EVENT_VENDORS', // #170 — the vendors working an event and the documents each owes
  INVENTORY: 'INVENTORY',
  TIMELINE_ITEMS: 'TIMELINE_ITEMS',
  TRAVEL_SEGMENTS: 'TRAVEL_SEGMENTS',
  TALLIES: 'TALLIES',
  RACE_TIMING: 'RACE_TIMING',
  EVENT_FILES: 'EVENT_FILES',
  EVENT_SCHEDULE: 'EVENT_SCHEDULE',
  TICKETS: 'TICKETS',
  TICKET_SALES: 'TICKET_SALES',
  DISCOUNT_CODES: 'DISCOUNT_CODES',
  COUPONS: 'COUPONS',
  RSVP_QUESTIONS: 'RSVP_QUESTIONS',
  GUESTS: 'GUESTS',
  GUEST_GROUPS: 'GUEST_GROUPS',
  RSVP_GROUPS: 'RSVP_GROUPS',
  RSVP_RESPONSES: 'RSVP_RESPONSES',
  COMMUNICATIONS: 'COMMUNICATIONS',
  EVENT_PARTNERS: 'EVENT_PARTNERS',
  EVENT_PARTNER_PACKAGES: 'EVENT_PARTNER_PACKAGES',
  EVENT_ARTISTS: 'EVENT_ARTISTS',
  EVENT_POLICIES: 'EVENT_POLICIES',
  EVENT_ANALYTICS: 'EVENT_ANALYTICS',
} as const;

export const FULL_RIGHTS = [
  OPERATION_RIGHTS.CREATE,
  OPERATION_RIGHTS.UPDATE,
  OPERATION_RIGHTS.REMOVE,
  OPERATION_RIGHTS.PRINT,
  OPERATION_RIGHTS.IMPORT,
  OPERATION_RIGHTS.EXPORT,
];

// Build a "full rights on every entity" preset (DRY — used for OWNER & ADMIN,
// which currently share access). New roles get their own tailored map.
const fullRightsOnAllEntities = Object.values(ENTITIES).reduce(
  (acc, entity) => {
    acc[entity] = FULL_RIGHTS;
    return acc;
  },
  {} as Record<string, string[]>,
);

export const USER_ROLE_RIGHTS_ENTITIES: Record<string, Record<string, string[]>> = {
  [USER_ROLES.OWNER]: fullRightsOnAllEntities,
  [USER_ROLES.ADMIN]: fullRightsOnAllEntities,
};
